import { useEffect } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';

import useCompanyContext from 'common/hooks/useCompanyContext';
import { throwIfErrorPresent } from 'features/Onboarding/utils';

import getIsCapitalDepositStepRequired from '../getIsCapitalDepositStepRequired';
import {
  onboardingVerificationShowCapDepStepQuery,
  verificationHomePrimaryApplicantQuery,
  verificationHomeQuery,
} from './graphql';
import {
  VerificationHomeBasicQueryResults,
  VerificationHomePAQueryResults,
  VerificationHomeQueryInput,
  VerificationHomeQueryResults,
  VerificationQueryHook,
} from './types';

const joinBasicAndPaData = (
  basicData: VerificationHomeBasicQueryResults,
  paData: VerificationHomePAQueryResults | undefined,
): VerificationHomeQueryResults => {
  return {
    ...(paData ?? {}),
    viewer: {
      ...basicData.viewer,
      ...(paData?.viewer ?? {}),
      company: {
        ...basicData.viewer.company,
        ...(paData?.viewer.company ?? {}),
      },
    },
  };
};

const useVerificationQuery: VerificationQueryHook = (isPrimaryApplicant) => {
  const { companyProfileId } = useCompanyContext();
  const {
    data: basicData,
    error: basicError,
    loading: basicLoading,
  } = useQuery<VerificationHomeBasicQueryResults, VerificationHomeQueryInput>(
    verificationHomeQuery,
    {
      fetchPolicy: 'network-only',
      skip: !companyProfileId,
      variables: { companyProfileId: companyProfileId ?? '' },
    },
  );
  const {
    data: paData,
    error: paError,
    loading: paLoading,
  } = useQuery<VerificationHomePAQueryResults, VerificationHomeQueryInput>(
    verificationHomePrimaryApplicantQuery,
    {
      fetchPolicy: 'network-only',
      skip: !companyProfileId || isPrimaryApplicant !== true,
      variables: { companyProfileId: companyProfileId ?? '' },
    },
  );
  throwIfErrorPresent(basicError, paError);

  const shouldReturnData =
    (isPrimaryApplicant === true &&
      basicLoading === false &&
      paLoading === false) ||
    (isPrimaryApplicant === false && basicLoading === false);
  const data =
    shouldReturnData && basicData !== undefined
      ? joinBasicAndPaData(basicData, paData)
      : undefined;
  const loading = isPrimaryApplicant === undefined || basicLoading || paLoading;

  const client = useApolloClient();

  // Using a default value of null to match the default value set anyway when
  // a decision hasn't been made on the loaded remote data.
  const cachedShowCapitalDepositStep =
    paData?.onboardingVerificationShowCapDepStep ?? null;
  const isCapitalDepositStepRequired =
    data === undefined ? undefined : getIsCapitalDepositStepRequired(data);
  useEffect(() => {
    if (isPrimaryApplicant === undefined) {
      // Skip until the decision about whether the user is a primary applicant has been loaded
      return;
    }
    if (
      isCapitalDepositStepRequired !== undefined &&
      cachedShowCapitalDepositStep === null
    ) {
      // Store the first loaded value for future use. The necessity to do the step
      // will go away after the step has been completed but for the rest of the session
      // we will remember whether the user was asked to carry out this step or not.
      client.writeQuery({
        data: {
          onboardingVerificationShowCapDepStep: isCapitalDepositStepRequired,
        },
        query: onboardingVerificationShowCapDepStepQuery,
      });
    } else if (paLoading === false && cachedShowCapitalDepositStep === null) {
      // No value has been written yet so the cache needs writing
      client.writeQuery({
        data: {
          onboardingVerificationShowCapDepStep: null,
        },
        query: onboardingVerificationShowCapDepStepQuery,
      });
    }
  }, [
    cachedShowCapitalDepositStep,
    isCapitalDepositStepRequired,
    client,
    isPrimaryApplicant,
    paLoading,
  ]);

  return { cachedShowCapitalDepositStep, data, isPrimaryApplicant, loading };
};

export default useVerificationQuery;
